// Shared by the console and the native app's loopback viewer. Escape is never locked.
export function commandModifier(remoteMac, platform = navigator.platform) {
  return /Mac|iPhone|iPad|iPod/.test(platform) && !remoteMac ? 'ctrl' : 'meta';
}

// Validate the entire logical paste before returning an iterator or sending any input.
export function createDesktopPaste(text) {
  const encoder = new TextEncoder();
  if (text.length > 1048576 || encoder.encode(text).length > 1048576)
    throw new Error('Paste supports up to 1 MiB of text.');
  if (text.includes('\0') || /[\uD800-\uDFFF]/u.test(text))
    throw new Error('Paste requires valid Unicode text without NUL characters.');
  const id = crypto.randomUUID();
  return (function* () {
    let chunk = '',
      bytes = 0,
      offset = 0;
    for (const character of text) {
      const size = encoder.encode(character).length;
      if (bytes + size > 4096) {
        yield { type: 'paste', id, offset, text: chunk, complete: false };
        offset += bytes;
        chunk = '';
        bytes = 0;
      }
      chunk += character;
      bytes += size;
    }
    if (chunk) yield { type: 'paste', id, offset, text: chunk, complete: true };
  })();
}

export const specialKeys = [
  { label: 'Send Esc', keys: ['escape'], title: 'Send Escape to the remote device' },
  { label: 'Tab', keys: ['tab'], title: 'Tab' },
  { label: 'Enter', keys: ['enter'], title: 'Enter' },
  { label: 'Bksp', keys: ['backspace'], title: 'Backspace' },
  { label: 'Del', keys: ['delete'], title: 'Delete' },
  { label: 'Home', keys: ['home'], title: 'Home' },
  { label: 'End', keys: ['end'], title: 'End' },
  { label: 'PgUp', keys: ['page_up'], title: 'Page up' },
  { label: 'PgDn', keys: ['page_down'], title: 'Page down' },
  ...Array.from({ length: 12 }, (_, i) => ({
    label: `F${i + 1}`,
    keys: [`f${i + 1}`],
    title: `F${i + 1}`,
  })),
  { label: 'Alt+Tab', keys: ['alt', 'tab'], title: 'Switch remote Windows window' },
  { label: 'Cmd+Tab', keys: ['meta', 'tab'], title: 'Switch remote macOS application' },
  { label: 'Alt+F4', keys: ['alt', 'f4'], title: 'Close remote Windows window' },
];

const lockedCodes = [
  ...Array.from('ABCDEFGHIJKLMNOPQRSTUVWXYZ', (letter) => `Key${letter}`),
  ...Array.from({ length: 10 }, (_, i) => `Digit${i}`),
  ...Array.from({ length: 12 }, (_, i) => `F${i + 1}`),
  'Tab',
  'Enter',
  'Space',
  'Backspace',
  'Delete',
  'Insert',
  'Home',
  'End',
  'PageUp',
  'PageDown',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'ControlLeft',
  'ControlRight',
  'AltLeft',
  'AltRight',
  'ShiftLeft',
  'ShiftRight',
  'MetaLeft',
  'MetaRight',
  'Backquote',
  'Minus',
  'Equal',
  'BracketLeft',
  'BracketRight',
  'Backslash',
  'Semicolon',
  'Quote',
  'Comma',
  'Period',
  'Slash',
];

/** @param {import('./desktop-input.js').CaptureOptions} options */
export function createInputCapture({ surface, active, release, suspend = release, unavailable }) {
  const pointers = new Set();
  let revision = 0,
    pending = false,
    disposed = false,
    escaped = false,
    fullscreen = !!document.fullscreenElement;
  const keyboard = navigator.keyboard;
  const eligible = () =>
    !disposed &&
    active() &&
    document.hasFocus() &&
    !document.hidden &&
    !!document.fullscreenElement &&
    surface.contains(document.activeElement);
  function unlock() {
    keyboard?.unlock?.();
  }
  function reset() {
    revision++;
    unlock();
    for (const id of pointers) {
      if (surface.hasPointerCapture(id)) surface.releasePointerCapture(id);
    }
    pointers.clear();
  }
  async function refresh() {
    if (!eligible()) {
      revision++;
      unlock();
      return;
    }
    if (pending || !keyboard?.lock) return;
    const current = ++revision;
    pending = true;
    try {
      await keyboard.lock(lockedCodes);
    } catch {
      if (current === revision && eligible()) unavailable();
    } finally {
      pending = false;
      if (current !== revision || !eligible()) {
        unlock();
        if (eligible()) void refresh();
      }
    }
  }
  function stop() {
    reset();
    release();
  }
  function suspendCapture() {
    suspend();
    reset();
  }
  function keydown(event) {
    if ((event.code !== 'Escape' && event.key !== 'Escape') || !active()) return;
    escaped = true;
    event.preventDefault();
    event.stopImmediatePropagation();
    stop();
  }
  function keyup(event) {
    if ((event.code !== 'Escape' && event.key !== 'Escape') || !escaped) return;
    escaped = false;
    event.preventDefault();
    event.stopImmediatePropagation();
  }
  function visibility() {
    if (document.hidden) suspendCapture();
  }
  function fullscreenChange() {
    const wasFullscreen = fullscreen;
    fullscreen = !!document.fullscreenElement;
    if (wasFullscreen && !fullscreen) suspendCapture();
    else void refresh();
  }
  function gotPointer(event) {
    pointers.add(event.pointerId);
  }
  function focusout(event) {
    if (event.relatedTarget instanceof Node && surface.contains(event.relatedTarget)) return;
    revision++;
    unlock();
  }
  function lostPointer(event) {
    pointers.delete(event.pointerId);
  }
  window.addEventListener('keydown', keydown, true);
  window.addEventListener('keyup', keyup, true);
  window.addEventListener('blur', suspendCapture);
  document.addEventListener('visibilitychange', visibility);
  document.addEventListener('fullscreenchange', fullscreenChange);
  document.addEventListener('focusin', refresh);
  surface.addEventListener('gotpointercapture', gotPointer);
  surface.addEventListener('lostpointercapture', lostPointer);
  surface.addEventListener('pointercancel', suspendCapture);
  surface.addEventListener('focusout', focusout);
  return {
    reset,
    refresh,
    dispose() {
      disposed = true;
      reset();
      window.removeEventListener('keydown', keydown, true);
      window.removeEventListener('keyup', keyup, true);
      window.removeEventListener('blur', suspendCapture);
      document.removeEventListener('visibilitychange', visibility);
      document.removeEventListener('fullscreenchange', fullscreenChange);
      document.removeEventListener('focusin', refresh);
      surface.removeEventListener('gotpointercapture', gotPointer);
      surface.removeEventListener('lostpointercapture', lostPointer);
      surface.removeEventListener('pointercancel', suspendCapture);
      surface.removeEventListener('focusout', focusout);
    },
  };
}
